import React, { useId } from 'react';

/** The ids a control needs to name and describe itself from the row's own text. */
export interface SettingRowControlIds {
    labelId: string;
    descriptionId?: string;
}

export interface SettingRowProps {
    label: string;
    /** Small badge after the label, e.g. a `Tag`. */
    badge?: React.ReactNode;
    /** Explanation rendered under the row, outside its border. */
    description?: React.ReactNode;
    /** The control on the trailing edge. Passed the row's ids: a control `<label htmlFor>`
     *  cannot reach must name itself with `aria-labelledby`. */
    control: React.ReactNode | ((ids: SettingRowControlIds) => React.ReactNode);
    className?: string;
}

export const SettingRow: React.FC<SettingRowProps> = ({
    label,
    badge,
    description,
    control,
    className = '',
}) => {
    const instanceId = useId();
    const labelId = `${instanceId}-label`;
    const descriptionId = description ? `${instanceId}-description` : undefined;

    return (
        <div className={className}>
            <div className="flex min-h-11 items-center gap-3 rounded-lg border border-[var(--malachite-card-border)] bg-[var(--malachite-card-background)] px-3 py-2">
                <span className="flex min-w-0 flex-1 items-center gap-2">
                    <span
                        id={labelId}
                        className="truncate text-sm text-[var(--malachite-text-primary)]"
                    >
                        {label}
                    </span>
                    {badge}
                </span>
                <span className="flex shrink-0 items-center">
                    {typeof control === 'function' ? control({ labelId, descriptionId }) : control}
                </span>
            </div>
            {description && (
                <p
                    id={descriptionId}
                    className="m-0 pt-2 text-[13px] leading-[1.5] text-[var(--malachite-text-secondary)]"
                >
                    {description}
                </p>
            )}
        </div>
    );
};

export default SettingRow;
